import { TopCustomer } from "../../services/dashboard";

interface TopCustomersTableProps {
  customers?: TopCustomer[];
  isLoading?: boolean;
  isError?: boolean;
}

const avatarPalette = [
  { bg: "#cce7ef", text: "#011b21" },
  { bg: "#adefe8", text: "#00201e" },
  { bg: "#ffdbc7", text: "#311300" },
  { bg: "#e3e3d9", text: "#1a1c16" },
  { bg: "#b1cbd3", text: "#011b21" },
];

const getInitials = (name: string) => {
  const parts = name.trim().split(" ");
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : parts[0].slice(0, 2).toUpperCase();
};

const TopCustomersTable = ({ customers, isLoading, isError }: TopCustomersTableProps) => {

  return (
    <div
      className="bg-white rounded-[8px] overflow-hidden flex flex-col w-full xl:flex-1 min-w-0"
      style={{ boxShadow: "0px 1px 2px rgba(0,0,0,0.05)" }}
    >
      {/* Card header */}
      <div className="px-6 py-6 bg-surface-input">
        <h2 className="text-[18px] font-semibold text-content-heading">
          Top 5 Customers
        </h2>
      </div>

      {/* Scrollable table area */}
      <div className="overflow-x-auto">
        <div className="min-w-[340px]">
          {/* Column headers */}
          <div className="flex items-center border-b border-[#f4f4ea]">
            <div className="flex-[2] px-6 py-[22px]">
              <span className="text-[12px] font-medium uppercase text-content-label">Customer Name</span>
            </div>
            <div className="flex-1 px-6 py-4 flex justify-end">
              <span className="text-[12px] font-medium uppercase text-content-label">Balance</span>
            </div>
          </div>

          {/* States */}
          {isLoading && (
            <p className="px-6 py-4 text-[13px] text-content-label">Loading…</p>
          )}
          {isError && (
            <p className="px-6 py-4 text-[13px] text-content-error">Failed to load customers.</p>
          )}
          {!isLoading && !isError && !customers?.length && (
            <p className="px-6 py-4 text-[13px] text-content-label">No customers yet.</p>
          )}

          {/* Data rows */}
          {customers?.filter((customer) => customer.balance !== 0).map((customer, i) => {
            const { bg, text } = avatarPalette[i % avatarPalette.length];
            return (
              <div
                key={customer.loyalty_id}
                className={`flex items-center ${i > 0 ? "border-t border-[#f4f4ea]" : ""}`}
              >
                <div className="flex-[2] px-4 py-4 flex items-center gap-3 min-w-0">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-[12px] font-medium"
                    style={{ backgroundColor: bg, color: text }}
                  >
                    {getInitials(customer.customer_name)}
                  </div>
                  <span className="text-[12px] font-medium truncate text-content-body">
                    {customer.customer_name}
                  </span>
                </div>
                <div className="flex-1 px-6 py-4 flex justify-end">
                  <span className="text-[14px] font-semibold text-right text-content-heading">
                    {customer.balance.toLocaleString()}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TopCustomersTable;
